import { useContext, useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import SingleSelectDropdown from "../../../../Components/CustomDropdowns/SingleSelectDropdown";
import { AppContext } from "../../../../Context/AppContext";
import { apiPutRequest } from "../../../../Helpers/Requests";
import endpoints from "../../../../Helpers/Endpoints";
import { useSnackbar } from "../../../../Components/SnackBar";
import {
  PRODUCT_STATUS,
  PRODUCT_STATUS_LABEL_KEY,
} from "../../../../Constants/productOrderStatus";
import useProductStatusMap from "../../../../hooks/useProductStatusMap";

// Selectable statuses, by NAME (Cancel is handled via the Decline action).
// "Ready for Pickup" is pickup-only; "Out for delivery" is delivery-only. The
// ids come from the status map the backend serves, never from this file.
const STATUS_OPTIONS = [
  { name: PRODUCT_STATUS.CONFIRMED },
  { name: PRODUCT_STATUS.PREPARING },
  { name: PRODUCT_STATUS.READY_FOR_PICKUP, pickupOnly: true },
  { name: PRODUCT_STATUS.OUT_FOR_DELIVERY, deliveryOnly: true },
  { name: PRODUCT_STATUS.COMPLETED },
].map((o) => ({ ...o, key: PRODUCT_STATUS_LABEL_KEY[o.name] }));

/**
 * Editable status dropdown for a product order in the supplier's Active Jobs
 * table. Persists the change to the backend (which also writes a status-history
 * row, driving the member-side status display).
 */
const ProductStatusDropdown = ({ job, onUpdated }) => {
  const { t } = useTranslation("page-my-jobs", { keyPrefix: "table_data" });
  const { profileData } = useContext(AppContext);
  const showSnackbar = useSnackbar();
  const [value, setValue] = useState(job.status?.id);
  const [saving, setSaving] = useState(false);

  const { idByName, ready } = useProductStatusMap(job.type);

  const isPickup = job.delivery_type === "pickup";
  const options = STATUS_OPTIONS.filter(
    (o) =>
      (!o.pickupOnly || isPickup) &&
      (!o.deliveryOnly || !isPickup) &&
      idByName[o.name] != null,
  ).map((o) => ({ value: idByName[o.name], label: t(o.key) }));

  const handleChange = async (newValue) => {
    if (newValue === value) return;
    const prev = value;
    setValue(newValue);
    setSaving(true);
    try {
      await apiPutRequest(
        endpoints.updateProductJobStatus(job.request_id, profileData.id),
        // `job.type` is 'product' | 'restaurant'; the BE needs it to pick the
        // vertical's order tables (without it a restaurant order 400s).
        { status_id: newValue, type: job.type },
      );
      // The row reflects the new status locally (above); pass the id up so the
      // parent only reloads the table when needed (e.g. Completed leaves Active).
      onUpdated?.(newValue);
    } catch (err) {
      console.error("Failed to update product status", err);
      setValue(prev);
      showSnackbar(t("status"), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box onClick={(e) => e.stopPropagation()}>
      <SingleSelectDropdown
        value={value}
        onChange={handleChange}
        options={options}
        disabled={saving || !ready}
        // Fixed width so the chevrons line up vertically across rows regardless
        // of the (varying-length) status label.
        width="150px"
        buttonHeight="auto"
        horizontalAnchorOrigin="right"
        sx={{
          // Minimal inline look (text + arrow only), per the Figma — no border
          // or box. The label sits at the leading edge and the arrow is pinned
          // to the trailing edge (base `space-between`), so arrows align.
          button: {
            border: "none",
            backgroundColor: "transparent",
            boxShadow: "none",
            outline: "none",
            paddingX: "8px",
            paddingY: "4px",
            // Never re-show the box border in ANY state. The disabled state is
            // the key one: while the status update is saving, the button is
            // disabled (and still open), and MUI's outlined `.Mui-disabled`
            // rule otherwise re-adds a 1px border for a moment.
            "&:hover, &:focus, &:focus-visible, &:active, &.Mui-focusVisible, &.Mui-disabled":
              {
                border: "none",
                outline: "none",
                boxShadow: "none",
                backgroundColor: "transparent",
              },
          },
          menuPaper: { minWidth: "max-content" },
        }}
      />
    </Box>
  );
};

export default ProductStatusDropdown;
